import { useEffect, useState } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Download } from "lucide-react";

export default function CashLedger() {
  const [data, setData] = useState({ entries: [], closing_balance: 0 });
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    const { data } = await api.get("/ledger", { params });
    setData(data); setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function csv() {
    const header = ["Date", "Ref No", "Type", "Particulars", "Debit", "Credit", "Balance"];
    const lines = data.entries.map((e) => [fmtDate(e.date), e.ref_no, e.type, e.particulars, e.debit, e.credit, e.balance]
      .map((x) => `"${String(x).replaceAll('"', '""')}"`).join(","));
    const blob = new Blob([[header.join(",")].concat(lines).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `cash-ledger_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  }

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900">Cash Ledger</h1>
          <p className="text-slate-500 mt-1">Chronological cash movements — inflow from receipts, outflow to bank/owner.</p>
        </div>
        <div className="stat-card-primary min-w-[240px]">
          <div className="text-xs uppercase text-blue-100 tracking-wider">Closing Balance</div>
          <div className="text-2xl font-extrabold mono mt-1">{fmtINR(data.closing_balance)}</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-2 text-sm" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-2 text-sm" />
        <button onClick={load} className="h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold">Apply</button>
        <button onClick={csv} className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-semibold flex items-center gap-2 ml-auto"><Download className="h-4 w-4" /> Export</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase text-slate-500 tracking-wider">
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4">Ref</th>
              <th className="py-3 px-4">Type</th>
              <th className="py-3 px-4">Particulars</th>
              <th className="py-3 px-4 text-right">Debit (Out)</th>
              <th className="py-3 px-4 text-right">Credit (In)</th>
              <th className="py-3 px-4 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && data.entries.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No entries</td></tr>}
            {data.entries.map((e) => (
              <tr key={`${e.ref_no}-${e.type}`} className="border-b border-slate-100">
                <td className="py-2.5 px-4 text-slate-600">{fmtDate(e.date)}</td>
                <td className="py-2.5 px-4 mono">{e.ref_no}</td>
                <td className="py-2.5 px-4">
                  <span className={`text-xs px-2 py-0.5 rounded-md font-semibold ${e.type === "CASH IN" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{e.type}</span>
                </td>
                <td className="py-2.5 px-4">{e.particulars}</td>
                <td className="py-2.5 px-4 text-right mono text-amber-700">{e.debit ? fmtINR(e.debit) : ""}</td>
                <td className="py-2.5 px-4 text-right mono text-emerald-700 font-semibold">{e.credit ? fmtINR(e.credit) : ""}</td>
                <td className="py-2.5 px-4 text-right mono font-bold">{fmtINR(e.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
